"use client";

import { LCatalogSingleColumn } from "./LCatalogSingleColumn/LCatalogSingleColumn";
import { LCatalogSingleColumnFull } from "./LCatalogSingleColumnFull/LCatalogSingleColumnFull";
import { LCatalogTwoColsLeft } from "./LCatalogTwoColsLeft/LCatalogTwoColsLeft";
import { LCatalogTwoColsRight } from "./LCatalogTwoColsRight/LCatalogTwoColsRight";
import { LCatalogThreeCols } from "./LCatalogThreeCols/LCatalogThreeCols";
import { LCatalogLanding } from "./LCatalogLanding/LCatalogLanding";
import { CatalogContentArea } from "./CatalogContentArea/CatalogContentArea";
import { WindowArea } from "../WindowArea/WindowArea";
import {
  getCatalogLayoutMeta,
  isCatalogLayoutSymbol,
  type CatalogLayoutSymbol,
} from "@/lib/layouts/types";
import { useAdmin } from "@/lib/admin/context";
import type { CatalogByPathMeta } from "@/lib/api/cms-catalog";
import type { FrontendUserSession } from "@/lib/api/session";

/**
 * CatalogLayoutRenderer — Client Component dispatcher catalog layouts.
 *
 * Czyta `currentLayout` z `AdminContext` — zmiana w select dropdownie
 * panelu admin -> instant re-render z nowym layoutem (bez revalidatePath).
 *
 * **Różnica vs HomepageLayoutRenderer:** middle slot ZAWSZE renderuje
 * `<CatalogContentArea />` (blocked, content z catalog management). Pozostałe
 * sloty (sidebars + belowCatalog) to `<WindowArea />` (drag&drop window slots).
 *
 * @param symbol — initial value (fallback gdy context ma non-catalog layout)
 * @param catalog — meta katalogu (z backend `IAPI_catalogByPath`) -> CatalogContentArea
 */
export function CatalogLayoutRenderer({
  symbol: initialSymbol,
  catalog,
}: CatalogLayoutRendererProps) {
  const { currentLayout } = useAdmin();
  const symbol: CatalogLayoutSymbol = isCatalogLayoutSymbol(currentLayout)
    ? currentLayout
    : initialSymbol;
  const meta = getCatalogLayoutMeta(symbol);
  const labelFor = (areaSymbol: string) =>
    meta.areas.find((a) => a.symbol === areaSymbol)?.name ?? areaSymbol;

  const above = <WindowArea name="aboveCatalog" label={labelFor("aboveCatalog")} />;
  const below = <WindowArea name="belowCatalog" label={labelFor("belowCatalog")} />;
  const content = (
    <CatalogContentArea
      title={catalog.title}
      subtitle={catalog.subtitle ?? null}
      componentSymbol={catalog.componentSymbol}
      content={catalog.content}
      background={catalog.background}
      meta={catalog.meta}
      userSession={catalog.userSession ?? null}
      containerSymbol={catalog.containerSymbol ?? null}
    />
  );
  const left = <WindowArea name="leftSidebar" label={labelFor("leftSidebar")} />;
  const right = <WindowArea name="rightSidebar" label={labelFor("rightSidebar")} />;

  switch (symbol) {
    case "LCatalogSingleColumn":
      return (
        <LCatalogSingleColumn
          catalogContent={content}
          belowCatalog={below}
        />
      );

    case "LCatalogSingleColumnFull":
      return (
        <LCatalogSingleColumnFull
          catalogContent={content}
          belowCatalog={below}
        />
      );

    case "LCatalogTwoColsLeft":
      return (
        <LCatalogTwoColsLeft
          leftSidebar={left}
          catalogContent={content}
          belowCatalog={below}
        />
      );

    case "LCatalogTwoColsRight":
      return (
        <LCatalogTwoColsRight
          catalogContent={content}
          belowCatalog={below}
          rightSidebar={right}
        />
      );

    case "LCatalogThreeCols":
      return (
        <LCatalogThreeCols
          leftSidebar={left}
          catalogContent={content}
          belowCatalog={below}
          rightSidebar={right}
        />
      );

    case "LCatalogLanding":
      return (
        <LCatalogLanding
          aboveCatalog={above}
          catalogContent={content}
          belowCatalog={below}
        />
      );
  }
}

export type CatalogLayoutRendererProps = {
  symbol: CatalogLayoutSymbol;
  /** Catalog metadata z backend `IAPI_catalogByPath` — uproszczony shape
      przekazany do `CatalogContentArea`. */
  catalog: CatalogMeta;
};

export type CatalogMeta = {
  /** `imp_cms_catalog.id` jako string (kompatybilność z dotychczasowym API). */
  id: string;
  /** Display name z `imp_cms_catalog.name`. */
  title: string;
  /** Podtytuł renderowany pod h1 w kontenerze katalogu z `imp_cms_catalog.name_subtitle`.
      NULL gdy admin nie wypełnił - CDefault pomija render, CEmpty ignoruje. */
  subtitle?: string | null;
  /** Symbol komponentu CMS — `imp_cms_component.symbol` (np. `c_descriptive_page`,
      `c_news`, `c_shop`). Determinuje React variant w `catalogVariants` registry
      (components/cms/registry/variants.tsx). */
  componentSymbol: string | null;
  /** Payload z `C_*_CONTROLLER::getApiContent()` — per-typ komponentu. */
  content: unknown;
  /** Tło katalogu z `imp_cms_catalog.config[background]` przez `m_site_backgrounds`.
      Backend `IAPI_catalogByPath` resolves do CSS-ready struct. */
  background?: import("@/lib/api/cms-windows").CmsBackground | null;
  /** Backend meta flagi (missing_class/missing_method/invalid_response) —
      `CatalogRenderer` używa do dispatch'u fallback message'y. */
  meta?: CatalogByPathMeta;
  /** SSR-resolved user session — wstrzykiwane przez `page.tsx` żeby c_*
      komponenty (np. `c_user_accounts`) widziały aktualny stan logowania
      bez osobnego client-side fetch'a. NULL gdy visitor (brak SSO cookie). */
  userSession?: FrontendUserSession | null;
  /** Symbol kontenera (wrapper HTML) z DB `imp_cms_container.symbol` -
      `IAPI_catalogByPath.data.container_symbol`. Backend lookup 2-warstwowy
      (V1 per-podstrona + V1 default per komponent). `CatalogRenderer` mapuje
      przez `resolveContainer` na `ContainerOverride` dla dispatch lambda. */
  containerSymbol?: string | null;
};
